/**
 * AnonymizationHistoryRow — linha do histórico de anonimizações de uma entidade.
 *
 * Renderiza SOMENTE campos reais do serializer (R15): id, level, status, datas
 * (requestedAt/startedAt/completedAt) e failureReason. As ações dependem do status
 * real: `pending` → Executar (irreversível, confirmação dura); `in_progress` →
 * Concluir; `held` → bloqueada por LegalHold. NÃO fabrica operationId/hash.
 */

import {
  ANONYMIZATION_LEVEL_META,
  ANONYMIZATION_STATUS_META,
  canComplete,
  canExecute,
  type AnonymizationWireItem,
} from './types';

interface AnonymizationHistoryRowProps {
  item: AnonymizationWireItem;
  onExecute: (item: AnonymizationWireItem) => void;
  onComplete: (item: AnonymizationWireItem) => void;
  isBusy: boolean;
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Parse defensivo do operationResult verídico do executor (Fatia 2). */
function parseOpResult(
  op: Record<string, unknown>,
): { fieldMapVersion: number; identities: number; structuredCollections: number } | null {
  const counts = op.counts;
  if (typeof counts !== 'object' || counts === null) return null;
  const c = counts as Record<string, unknown>;
  return {
    fieldMapVersion: typeof op.fieldMapVersion === 'number' ? op.fieldMapVersion : 0,
    identities: typeof c.identities === 'number' ? c.identities : 0,
    structuredCollections:
      typeof c.structuredCollections === 'number' ? c.structuredCollections : 0,
  };
}

export function AnonymizationHistoryRow({
  item,
  onExecute,
  onComplete,
  isBusy,
}: AnonymizationHistoryRowProps): JSX.Element {
  const status = ANONYMIZATION_STATUS_META[item.status];
  const opResult = item.status === 'completed' ? parseOpResult(item.operationResult) : null;

  return (
    <div className="anon-row">
      <div className="anon-row__head">
        <span className="anon-row__id" title={item.id}>
          {item.id.slice(0, 14)}
        </span>
        <span className={`anon-status anon-status--${status.tone}`}>{status.label}</span>
        <span className="anon-row__level">{ANONYMIZATION_LEVEL_META[item.level].label}</span>
      </div>

      <div className="anon-row__grid">
        <span className="anon-row__key">solicitada</span>
        <span className="anon-row__val">{formatDateTime(item.requestedAt)}</span>
        <span className="anon-row__key">iniciada</span>
        <span className="anon-row__val">{formatDateTime(item.startedAt)}</span>
        <span className="anon-row__key">concluída</span>
        <span className="anon-row__val">{formatDateTime(item.completedAt)}</span>
      </div>

      {item.failureReason !== null && (
        <p className="anon-row__failure" role="alert">
          {item.failureReason}
        </p>
      )}

      {opResult !== null && (
        <p className="anon-row__opresult">
          Efetuado (mapa v{opResult.fieldMapVersion}): {opResult.identities} identidade(s) +{' '}
          {opResult.structuredCollections} coleta(s) redatadas junto com a pessoa.
        </p>
      )}

      {item.status === 'held' && (
        <p className="anon-row__held">
          Bloqueada por LegalHold ativo — levante o hold antes de executar.
        </p>
      )}

      <div className="anon-row__actions">
        {canExecute(item.status) && (
          <button
            type="button"
            className="anon-btn anon-btn--danger"
            disabled={isBusy}
            onClick={() => onExecute(item)}
          >
            Executar anonimização
          </button>
        )}
        {canComplete(item.status, item.entityType) && (
          <button
            type="button"
            className="anon-btn anon-btn--primary"
            disabled={isBusy}
            onClick={() => onComplete(item)}
          >
            Concluir
          </button>
        )}
      </div>
    </div>
  );
}
